/**
 * sessionStorage / localStorage 래퍼.
 *
 * - SSR·프라이빗 모드·스토리지 차단 환경에서 접근만 해도 throw 하는 브라우저가 있어
 *   생성 시 한 번 접근 테스트를 하고, 실패하면 이후 모든 호출을 조용히 no-op 처리한다.
 * - 값은 { v, e } 봉투에 담아 저장하므로 키 단위 TTL 을 걸 수 있다.
 */

type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [k: string]: JsonValue };

export interface StorageOptions {
  /** 키 접두사 (예: 'app:') */
  prefix?: string;
  /** 기본 TTL(ms). 지정하면 set() 시 만료시각을 함께 저장한다. */
  defaultTtlMs?: number;
  /** 직렬화/역직렬화 실패를 삼키지 않고 던질지 */
  throwOnError?: boolean;
  /** 백엔드 스토리지 종류 */
  driver?: 'session' | 'local';
}

interface Envelope<T> {
  /** value */
  v: T;
  /** expiresAt (epoch ms). 없으면 만료 없음 */
  e?: number;
}

export class KeyValueStore<K extends string = string> {
  private readonly storage: Storage | null;
  private readonly prefix: string;
  private readonly defaultTtlMs?: number;
  private readonly throwOnError: boolean;

  constructor(opts: StorageOptions = {}) {
    this.prefix = opts.prefix ?? '';
    this.defaultTtlMs = opts.defaultTtlMs;
    this.throwOnError = opts.throwOnError ?? false;
    this.storage = KeyValueStore.resolveStorage(opts.driver ?? 'session');
  }

  /** 접근 가능한 Storage 인지 실제 write 로 확인한다. */
  private static resolveStorage(driver: 'session' | 'local'): Storage | null {
    try {
      if (typeof window === 'undefined') return null;
      const store = driver === 'local' ? window.localStorage : window.sessionStorage;
      if (!store) return null;

      const probe = '__kv_probe__';
      store.setItem(probe, '1');
      store.removeItem(probe);
      return store;
    } catch {
      return null;
    }
  }

  private key(key: K): string {
    return `${this.prefix}${key}`;
  }

  private fail(err: unknown): void {
    if (this.throwOnError) throw err;
  }

  /** 만료됐으면 삭제하고 true */
  private purgeIfExpired(key: K, env: Envelope<unknown>): boolean {
    if (typeof env.e === 'number' && Date.now() > env.e) {
      this.remove(key);
      return true;
    }
    return false;
  }

  /** 봉투 없이 원본 문자열 저장 */
  setString(key: K, value: string): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(this.key(key), value);
    } catch (err) {
      this.fail(err);
    }
  }

  getString(key: K): string | null {
    if (!this.storage) return null;
    try {
      return this.storage.getItem(this.key(key));
    } catch (err) {
      this.fail(err);
      return null;
    }
  }

  /** JSON 값 저장 (+ 선택적 TTL) */
  set<T extends JsonValue>(key: K, value: T, ttlMs?: number): void {
    if (!this.storage) return;

    const ttl = ttlMs ?? this.defaultTtlMs;
    const env: Envelope<T> = {
      v: value,
      ...(typeof ttl === 'number' ? { e: Date.now() + ttl } : {}),
    };

    try {
      this.storage.setItem(this.key(key), JSON.stringify(env));
    } catch (err) {
      this.fail(err);
    }
  }

  /** JSON 값 읽기. 만료·파싱실패는 null */
  get<T = unknown>(key: K): T | null {
    if (!this.storage) return null;

    try {
      const raw = this.storage.getItem(this.key(key));
      if (raw == null) return null;

      const parsed: unknown = JSON.parse(raw);

      // 봉투 형태면 만료를 검사한다.
      if (parsed && typeof parsed === 'object' && 'v' in parsed) {
        const env = parsed as Envelope<T>;
        if (this.purgeIfExpired(key, env)) return null;
        return env.v ?? null;
      }

      // 봉투 없이 저장된 레거시 값
      return parsed as T;
    } catch (err) {
      this.fail(err);
      return null;
    }
  }

  /** 존재 여부 (만료면 false) */
  has(key: K): boolean {
    return this.get(key) !== null;
  }

  remove(key: K): void {
    if (!this.storage) return;
    try {
      this.storage.removeItem(this.key(key));
    } catch (err) {
      this.fail(err);
    }
  }

  /** prefix 범위의 모든 키를 훑는다. */
  private prefixedKeys(): string[] {
    const keys: string[] = [];
    if (!this.storage) return keys;

    for (let i = 0; i < this.storage.length; i += 1) {
      const k = this.storage.key(i);
      if (!k) continue;
      if (this.prefix && !k.startsWith(this.prefix)) continue;
      keys.push(k);
    }
    return keys;
  }

  /** prefix 범위 전체 삭제 */
  clearPrefix(): void {
    if (!this.storage) return;
    try {
      for (const k of this.prefixedKeys()) this.storage.removeItem(k);
    } catch (err) {
      this.fail(err);
    }
  }

  /** prefix 범위에서 만료된 항목 정리 */
  cleanupExpired(): void {
    if (!this.storage) return;

    try {
      for (const fullKey of this.prefixedKeys()) {
        const raw = this.storage.getItem(fullKey);
        if (!raw) continue;

        try {
          const env = JSON.parse(raw) as Envelope<unknown>;
          if (env && typeof env === 'object' && typeof env.e === 'number' && Date.now() > env.e) {
            this.storage.removeItem(fullKey);
          }
        } catch {
          // JSON 이 아닌 값은 건드리지 않는다.
        }
      }
    } catch (err) {
      this.fail(err);
    }
  }
}
